import { signInWithCustomToken, type Auth } from 'firebase/auth';

export const PF_MOBILE_AUTH_EVENT = 'pf-mobile-auth';
export const PF_MOBILE_BRIDGE_FLAG = '__PF_MOBILE_BRIDGE__';
export const PF_MOBILE_AUTH_PARAM = 'pfMobileAuth';

type MobileBridge = {
	version: number;
	applyCustomToken?: (token: string) => void;
	pendingToken?: string | null;
};

declare global {
	interface Window {
		[PF_MOBILE_BRIDGE_FLAG]?: MobileBridge;
	}
}

/** Pull a one-time token from ?pfMobileAuth= (Expo secure browser) and scrub it from the URL. */
export function consumeMobileAuthFromUrl(href: string = typeof window !== 'undefined' ? window.location.href : ''): string | null {
	if (!href) return null;
	try {
		const url = new URL(href);
		const token = url.searchParams.get(PF_MOBILE_AUTH_PARAM);
		if (!token) return null;
		url.searchParams.delete(PF_MOBILE_AUTH_PARAM);
		const next = `${url.pathname}${url.search}${url.hash}`;
		if (typeof window !== 'undefined' && window.history?.replaceState) {
			window.history.replaceState({}, '', next || '/');
		}
		return token;
	} catch {
		return null;
	}
}

/** True when the current URL still carries a mobile handoff token (before consume). */
export function hasMobileAuthInUrl(href: string = typeof window !== 'undefined' ? window.location.href : ''): boolean {
	if (!href) return false;
	try {
		return Boolean(new URL(href).searchParams.get(PF_MOBILE_AUTH_PARAM));
	} catch {
		return false;
	}
}

export async function signInWithMobileAuthToken(auth: Auth, token: string): Promise<void> {
	const trimmed = token.trim();
	if (!trimmed) return;
	if (auth.currentUser) return;
	await signInWithCustomToken(auth, trimmed);
}

/**
 * Installs the bridge the Expo WebView injects into, and signs in when a
 * custom token arrives (URL param / postMessage / injected JS / pending token).
 *
 * When a URL token is present, returns a Promise that resolves after that
 * sign-in attempt finishes so AuthProvider can keep showing the loader and
 * avoid a race to /login.
 */
export function installMobileWebViewAuth(auth: Auth): { cleanup: () => void; handoff: Promise<void> } {
	if (typeof window === 'undefined') {
		return { cleanup: () => undefined, handoff: Promise.resolve() };
	}

	let cancelled = false;

	const applyToken = async (token: string) => {
		if (cancelled) return;
		try {
			await signInWithMobileAuthToken(auth, token);
		} catch (err) {
			console.warn('pf-mobile auth bridge failed:', err);
		}
	};

	const bridge: MobileBridge = window[PF_MOBILE_BRIDGE_FLAG] ?? { version: 1 };
	bridge.version = 1;
	bridge.applyCustomToken = (token: string) => {
		void applyToken(token);
	};
	window[PF_MOBILE_BRIDGE_FLAG] = bridge;

	if (bridge.pendingToken) {
		const pending = bridge.pendingToken;
		bridge.pendingToken = null;
		void applyToken(pending);
	}

	// Consume synchronously so RouteGuard never sees an unauthenticated URL flash
	// with the token still sitting in the query string after a redirect.
	const fromUrl = consumeMobileAuthFromUrl();
	const handoff = fromUrl ? applyToken(fromUrl) : Promise.resolve();

	const onCustomEvent = (event: Event) => {
		const detail = (event as CustomEvent<string>).detail;
		if (typeof detail === 'string') void applyToken(detail);
	};

	const onDocumentMessage = (event: MessageEvent | Event) => {
		const data = 'data' in event ? event.data : undefined;
		if (typeof data !== 'string') return;
		try {
			const parsed = JSON.parse(data) as { type?: string; customToken?: string };
			if (parsed?.type === 'PF_MOBILE_AUTH' && parsed.customToken) {
				void applyToken(parsed.customToken);
			}
		} catch {
			/* ignore non-JSON messages */
		}
	};

	window.addEventListener(PF_MOBILE_AUTH_EVENT, onCustomEvent as EventListener);
	window.addEventListener('message', onDocumentMessage as EventListener);
	document.addEventListener('message', onDocumentMessage as EventListener);

	return {
		cleanup: () => {
			cancelled = true;
			window.removeEventListener(PF_MOBILE_AUTH_EVENT, onCustomEvent as EventListener);
			window.removeEventListener('message', onDocumentMessage as EventListener);
			document.removeEventListener('message', onDocumentMessage as EventListener);
		},
		handoff,
	};
}
